import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import nestedStyles from "./NestedTable.module.scss";
import styles from "./Table.module.scss";

import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import { TableColumn } from "@/types/table/tableProps";

interface NestedTableProps<T, C> {
  columns: TableColumn[];
  data: T[];
  direction?: "asc" | "desc";
  getNestedData: (row: T) => C[];
  isLoading?: boolean;
  idField: keyof T;
  nestedColumns: TableColumn[];
  onRowClicked?: (row: T) => void;
  onSort?: (column: string) => void;
  onToggleRow?: (row: T, isExpanding: boolean) => void;
  openedRowId?: string | null;
  orderBy?: string | null;
}

const NestedTable = <T, C>({
  columns,
  data,
  direction,
  getNestedData,
  isLoading,
  idField,
  nestedColumns,
  onRowClicked,
  onSort,
  onToggleRow,
  openedRowId,
  orderBy,
}: NestedTableProps<T, C>) => {
  // Only used in uncontrolled mode
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);
  const { t } = useTranslation();

  const handleSort = (column: string) => {
    onSort?.(column);
  };

  const toggleRowExpansion = (
    rowIndex: number,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined
  ) => {
    event?.stopPropagation();

    // If we have a dedicated toggle handler, use it
    if (onToggleRow && data[rowIndex]) {
      // Determine if we're expanding or collapsing
      const isCurrentlyExpanded = isRowExpanded(rowIndex);
      // Pass row and the new state (opposite of current state)
      onToggleRow(data[rowIndex], !isCurrentlyExpanded);
      return;
    }

    // If we're in controlled mode but no toggle handler, fall back to row click handler
    if (openedRowId !== undefined && data[rowIndex] && onRowClicked) {
      onRowClicked(data[rowIndex]);
      return;
    }

    // For uncontrolled mode
    setExpandedRowIndex((prevIndex) =>
      prevIndex === rowIndex ? null : rowIndex
    );
  };

  const isRowExpanded = (rowIndex: number) => {
    // If in controlled mode (openedRowId is provided), use it to determine expanded row
    if (openedRowId !== undefined) {
      return data[rowIndex]?.[idField]?.toString() === openedRowId?.toString();
    }

    // Otherwise use internal state (uncontrolled mode)
    return rowIndex === expandedRowIndex;
  };

  const updatedColumns = columns.map((column) => ({
    ...column,
    sortable: column.sortable ?? true,
  }));

  return (
    <div
      className={`${styles.tableContainer} ${nestedStyles.nestedTableContainer}`}
    >
      <table>
        {/* Fixed column structure */}
        <colgroup>
          <col
            className={nestedStyles.expandColDefinition}
            style={{ width: "40px" }}
          />
          {updatedColumns.map((column, index) => (
            <col
              key={index}
              style={{ width: column.width ? column.width : "auto" }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {updatedColumns.map((column, index) => {
              return (
                <th
                  key={index}
                  scope="col"
                  className={`${styles.headerPointer} ${
                    column.textAlign === "right" ? styles.alignRight : ""
                  }`}
                  style={{
                    width: column.width ? `${column.width}` : "100%",
                  }}
                  onClick={() => {
                    if (column.sortable !== false && column.accessor) {
                      handleSort(column.accessor);
                    }
                  }}
                >
                  <span className={`${styles.headerWrapper}`}>
                    {column.header}
                    {orderBy && orderBy === column.accessor && (
                      <span>
                        {direction === "desc" ? (
                          <Icons.ArrowDownIcon fontSize={24} />
                        ) : (
                          <Icons.ArrowUpIcon fontSize={24} />
                        )}
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <Skeleton
              columns={[...columns]}
              totalRows={10}
              hasExpandColumn={false}
            />
          ) : data === undefined || data.length === 0 ? (
            <tr>
              <td className={styles.noData}>{t("pagination.noItems")}</td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <React.Fragment key={String(row[idField])}>
                <tr
                  className={
                    isRowExpanded(rowIndex) ? nestedStyles.expandedRow : ""
                  }
                  onClick={() => onRowClicked && onRowClicked(row)}
                >
                  {columns.map((column, colIndex) => {
                    return colIndex === 0 ? (
                      <td key={colIndex}>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <Button
                            variant="outline"
                            size="icon-sm"
                            onClick={(e) => toggleRowExpansion(rowIndex, e)}
                          >
                            {isRowExpanded(rowIndex) ? (
                              <Icons.ChevronUpIcon fontSize={24} />
                            ) : (
                              <Icons.ChevronDownIcon fontSize={24} />
                            )}
                          </Button>
                          {column.Cell
                            ? column.Cell(row, rowIndex)
                            : String(
                              row[column.accessor as keyof typeof row] ?? ""
                            )}
                        </span>
                      </td>
                    ) : (
                      <td key={colIndex}>
                        {column.Cell
                          ? column.Cell(row, rowIndex)
                          : String(
                            row[column.accessor as keyof typeof row] ?? ""
                          )}
                      </td>
                    );
                  })}
                </tr>
                {isRowExpanded(rowIndex) && (
                  <tr className={nestedStyles.nestedRow}>
                    <td colSpan={columns.length + 1}>
                      <div className={nestedStyles.nestedTableWrapper}>
                        <table className={nestedStyles.nestedTable}>
                          <colgroup>
                            {nestedColumns.map((column, index) => (
                              <col
                                key={index}
                                style={{
                                  width: column.width ? column.width : "auto",
                                }}
                              />
                            ))}
                          </colgroup>
                          <thead>
                            <tr>
                              {nestedColumns.map((column, index) => (
                                <th
                                  key={index}
                                  className={`${styles.headerPointer} ${
                                    column.textAlign === "right"
                                      ? styles.alignRight
                                      : ""
                                  }`}
                                  style={{
                                    width: column.width
                                      ? `${column.width}`
                                      : "100%",
                                  }}
                                >
                                  <span>{column.header}</span>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <Skeleton
                                columns={[...nestedColumns]}
                                totalRows={10}
                              />
                            ) : getNestedData(row).length === 0 ? (
                              <tr>
                                <td
                                  colSpan={nestedColumns.length}
                                  className={styles.noData}
                                >
                                  {t("pagination.noItems")}
                                </td>
                              </tr>
                            ) : (
                              getNestedData(row).map(
                                (nestedRow, nestedRowIndex) => (
                                  <tr key={`nested-row-${nestedRowIndex}`}>
                                    {nestedColumns.map((column, colIndex) => (
                                      <td key={colIndex}>
                                        {column.Cell
                                          ? column.Cell(
                                            nestedRow,
                                            nestedRowIndex
                                          )
                                          : String(
                                            nestedRow[
                                                column.accessor as keyof typeof nestedRow
                                            ] ?? ""
                                          )}
                                      </td>
                                    ))}
                                  </tr>
                                )
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default NestedTable;

interface TableSkeletonProps {
  totalRows: number;
  columns: TableColumn[];
  hasExpandColumn?: boolean;
}

const Skeleton = ({
  totalRows,
  columns,
  hasExpandColumn = false,
}: TableSkeletonProps) => {
  return Array.from({ length: totalRows }).map((_, rowIndex) => (
    <tr key={`skeleton-row-${rowIndex}`} className={styles.skeletonRow}>
      {hasExpandColumn && (
        <td className={nestedStyles.expandColumn} style={{ width: "40px" }}>
          <div className={nestedStyles.skeletonButton}></div>
        </td>
      )}
      {columns.map((column, colIndex) => (
        <React.Fragment key={`skeleton-row-${rowIndex}-col-${colIndex}`}>
          {column.accessor !== "spacer" ? (
            <td
              className={`${styles.skeletonCell} ${
                column.accessor === "actions" && styles.actionCell
              }`}
              style={{
                width: column.width ? `${column.width}` : "100%",
              }}
            >
              <div></div>
            </td>
          ) : (
            <td></td>
          )}
        </React.Fragment>
      ))}
    </tr>
  ));
};
